import { Spin, Tree } from 'antd'
import { DataNode } from 'antd/lib/tree'
import { uniq } from 'lodash'
import { isNaN } from 'lodash'
import React from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom'

import {
  useGetFavoredKnowledgeGroupsQuery,
  useGetKnowledgeGroupsQuery,
  useSwitchKnowledgeGroupMutation,
} from '@/generated/graphql'
import { routes } from '@/routes'

import s from './knowledge-catalog.module.less'

export const KnowledgeCatalog: React.FC<{
  setFavorGroupKey?: React.Dispatch<React.SetStateAction<number | undefined>>
  type: 'sidebar' | 'favor'
}> = ({ setFavorGroupKey, type }) => {
  const history = useHistory()
  const match = useRouteMatch<{ categoryId: string }>('/knowledgebase/categories/:categoryId')

  const categoryId = parseInt(match?.params.categoryId || '')

  const [treeNode, setTreeNode] = React.useState<DataNode[]>([])
  const [rootKeys, setRootKeys] = React.useState<string[]>([])
  const [checkedKeys, setCheckedKeys] = React.useState<string[]>([])
  const [expandedKeys, setExpandedKeys] = React.useState<string[]>([])
  const [selectedKeys, setSelectedKeys] = React.useState<string[]>([])

  const { data, loading, refetch } = useGetKnowledgeGroupsQuery({
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      pagination: {
        last: 2000, // 一次获取所有信息
      },
    },
  })

  const { data: favorGroupData } = useGetFavoredKnowledgeGroupsQuery({
    skip: type !== 'favor',
  })

  const [switchGroup] = useSwitchKnowledgeGroupMutation({
    onCompleted() {
      refetch()
    },
  })

  const favorGroups =
    favorGroupData && favorGroupData.viewer.favoredKnowledgeGroups ? favorGroupData.viewer.favoredKnowledgeGroups : []

  const knowledgeGroupMenus = data ? data.knowledge.groups.edges.map(({ node }) => node) : []

  function getTreeNodeData(childrenIds: number[]): DataNode[] {
    return knowledgeGroupMenus
      .filter((group) => childrenIds.includes(group.knowledgeGroupId))
      .sort((a, b) => a.position - b.position)
      .filter((child) => child)
      .map((child) => ({
        key: `${child.knowledgeGroupId}`,
        title: child.name + ' (' + child.posts.totalCount + ')',
        disableCheckbox: favorGroups.some((favorGroup) => favorGroup.knowledgeGroupId === child.knowledgeGroupId),
        ...(child.childGroupIds && {
          children: getTreeNodeData(child.childGroupIds),
        }),
      }))
  }

  const loadingComponent = loading && (
    <div className={s.spin}>
      <Spin size='large' delay={300} />
    </div>
  )

  React.useEffect(() => {
    if (data) {
      const nodes: DataNode[] = []
      const roots = knowledgeGroupMenus
        .filter((group) => !group.parentKnowledgeGroupId)
        .sort((a, b) => a.position - b.position)
      setRootKeys(roots.map((root) => `${root.knowledgeGroupId}`))
      setExpandedKeys(roots.map((root) => `${root.knowledgeGroupId}`))
      for (const root of roots) {
        const node = {
          key: `${root.knowledgeGroupId}`,
          title: root.name + ' (' + root.posts.totalCount + ')',
          disableCheckbox: favorGroups.some((favorGroup) => favorGroup.knowledgeGroupId === root.knowledgeGroupId),
          ...(root.childGroupIds && {
            children: getTreeNodeData(root.childGroupIds),
          }),
        }
        nodes.push(node)
      }
      setTreeNode(nodes)
    }
  }, [data])

  React.useEffect(() => {
    if (type !== 'favor') {
      if (!isNaN(categoryId)) {
        const key = `${categoryId}`
        if (!expandedKeys.includes(key)) {
          const path =
            knowledgeGroupMenus.find((knowledgeGroupMenu) => knowledgeGroupMenu.knowledgeGroupId === categoryId)
              ?.path || ''
          const parentKeys = path.split('-').filter(Boolean)

          setExpandedKeys(uniq([...expandedKeys, ...parentKeys]))
        }
        setSelectedKeys([key])
      } else {
        setSelectedKeys([])
      }
    }
  }, [categoryId, data])

  const tree = (
    <Tree
      draggable={true}
      onDrop={(info) => {
        const dragPosition = info.dropPosition
        const { key: dragNodeKey } = info.dragNode
        const { key: nodeKey } = info.node
        const source = knowledgeGroupMenus.find((menu) => Number(dragNodeKey) === menu.knowledgeGroupId)
        const target = knowledgeGroupMenus.find((menu) => Number(nodeKey) === menu.knowledgeGroupId)

        // 平级移动
        if (source!.parentKnowledgeGroupId === target!.parentKnowledgeGroupId) {
          switchGroup({
            variables: {
              sourceId: Number(dragNodeKey),
              targetId: Number(nodeKey),
              includeTarget: dragPosition === -1, // 最上层移动到第一个, targetNode 为当前目录第一个， dragPosition 会变为 -1
              ...(!!source!.parentKnowledgeGroupId && { parentId: source!.parentKnowledgeGroupId }),
            },
          })
          // 移动到父级目录第一个 targetNode 会变为父级节点
        } else if (source?.parentKnowledgeGroupId === target?.knowledgeGroupId) {
          const groups = knowledgeGroupMenus
            .filter((menu) => menu.parentKnowledgeGroupId === target?.knowledgeGroupId)
            .sort((a, b) => a.position - b.position)
          switchGroup({
            variables: {
              sourceId: Number(dragNodeKey),
              targetId: Number(groups[0].knowledgeGroupId),
              parentId: target?.knowledgeGroupId,
              includeTarget: true,
            },
          })
        }
      }}
      treeData={treeNode}
      titleRender={(node) => (
        <span className={rootKeys.includes(node.key as string) ? s.rootNodes : undefined}>{node.title}</span>
      )}
      expandedKeys={expandedKeys}
      selectedKeys={selectedKeys}
      checkedKeys={checkedKeys}
      checkable={type === 'favor'}
      checkStrictly
      onCheck={(value) => {
        const checkedCount = value['checked'].length
        const key = value['checked'][value['checked'].length - 1]

        if (checkedCount === 0) {
          setCheckedKeys([])
          setFavorGroupKey!(undefined)
        } else {
          setCheckedKeys([key])
          setFavorGroupKey!(Number(key))
        }
      }}
      onExpand={(expandedKeys) => setExpandedKeys(expandedKeys.map((expandedKey) => expandedKey as string))}
      {...(type === 'sidebar' && {
        onSelect: ([key]) => {
          history.push(key ? routes.knowledgebase.category(key) : routes.knowledgebase.home())
          setSelectedKeys([key as string])
        },
      })}
    />
  )

  return (
    <div className={s.tree}>
      {loadingComponent}
      {tree}
    </div>
  )
}
